import { useState, useEffect, ReactNode } from 'react'
import { isAuthApiError, Session } from '@supabase/supabase-js'
import { supabaseApi } from '~/supabase/api'
import { Box, Button, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'
import { ControlledTextField } from '~/ui/components/ControlledTextField'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useNotifyError } from '~/features/hooks/useNotifyError'
import { USER_ROLE_QUERY_KEY } from '~/features/auth/useRole'

type Payload = { email: string; password: string }

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    // getSession only reads localStorage, so verify the stored session with the auth server.
    // PostgREST accepts any validly signed JWT, so a deleted user would otherwise still see data.
    const loadSession = async () => {
      const {
        data: { session },
      } = await supabaseApi.auth.getSession()
      const { error } = session ? await supabaseApi.auth.getUser() : { error: null }
      const isRejected = isAuthApiError(error) && (error.status === 401 || error.status === 403)

      if (isRejected) {
        // Local scope: the server no longer knows this user, so there is nothing to revoke there
        await supabaseApi.auth.signOut({ scope: 'local' })
      }

      setSession(isRejected ? null : session)
      setIsLoading(false)
    }
    loadSession()

    const {
      data: { subscription },
    } = supabaseApi.auth.onAuthStateChange((event, session) => {
      // The initial session is handled (and verified) by loadSession
      if (event === 'INITIAL_SESSION') return
      setSession(session)
      setIsLoading(false)
      // A new session means a new JWT with a potentially different user_role claim.
      queryClient.invalidateQueries({ queryKey: [USER_ROLE_QUERY_KEY] })
    })
    return () => subscription.unsubscribe()
  }, [queryClient])

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<Payload>({
    defaultValues: {
      email: '',
      password: '',
    },
  })
  const { mutate: notifyError } = useNotifyError()
  const { mutate, isPending } = useMutation({
    mutationFn: async (payload: Payload) => {
      const { error } = await supabaseApi.auth.signInWithPassword(payload)

      if (error) {
        toast.error(error.message)
      }
    },
    onError: (error) => {
      toast.error('Something went wrong')
      console.error(error)
      notifyError({
        error,
        message: 'Login failed. Something went wrong',
        function: 'useMutation in AuthProvider',
        data: { payload: watch() },
      })
    },
  })
  const logIn = (data: Payload) => {
    mutate(data)
  }
  if (!session) {
    return isLoading ? null : (
      <Stack
        height={1}
        width={1}
        justifyContent="center"
        alignItems="center"
        component="form"
        direction="row"
        onSubmit={handleSubmit(logIn)}
        sx={{
          backgroundColor: 'hsla(357,0%,98%,1)',
          backgroundImage: `radial-gradient(at 70% 44%, hsla(0,0%,98%,1) 0px, transparent 50%),
radial-gradient(at 72% 88%, hsla(0,0%,90%,1) 0px, transparent 50%),
radial-gradient(at 20% 30%, hsla(0,7%,90%,1) 0px, transparent 50%)`,
        }}
      >
        <Stack
          flex={1}
          gap={3}
          maxWidth={{ sm: 500 }}
          minHeight={600}
          textAlign="center"
          component={Paper}
          sx={{
            backgroundColor: 'background.default',
            px: { xs: 2, sm: 6 },
            py: 4,
            boxShadow: '0px 4px 18px 3px rgba(0,0,0,0.05)',
          }}
          elevation={10}
          my={4}
          mx={2}
        >
          <Box component="img" src="wollem_logo.png" sx={{ width: 100, alignSelf: 'center' }} />
          <Stack divider={<Divider />} gap={4}>
            <Stack gap={3}>
              <Typography variant="h5">Log in</Typography>

              <ControlledTextField
                control={control}
                name="email"
                label="email"
                errors={errors}
                fullWidth
                size="medium"
              />
              <ControlledTextField
                control={control}
                name="password"
                label="password"
                errors={errors}
                fullWidth
                type="password"
                size="medium"
              />
              <Button
                type="submit"
                endIcon={isPending ? <CircularProgress size={14} /> : null}
                disabled={isPending}
                variant="contained"
              >
                Log in
              </Button>
            </Stack>
            <Typography variant="body2"> Copyright © 2025 Wollem</Typography>
          </Stack>
        </Stack>
      </Stack>
    )
  } else {
    return children
  }
}
